/**
 * The stage palette.
 *
 * Fixed values, not CSS custom properties, and deliberately so: six labs draw to
 * a `<canvas>`, and `ctx.fillStyle` cannot resolve `var(--x)`. A TypeScript
 * constant is the one form that works in a style object and in a canvas call,
 * which is what lets every lab read from a single definition instead of the 546
 * hardcoded hex values that were here before.
 *
 * These do not follow the theme. They live inside `.lab-stage`, which stays dark
 * in both themes because the demos render terminal output and colour-coded state
 * authored light-on-dark — see the note in lab.css. The page around the stage
 * themes normally.
 */

const RAW = {
    /** Page ground inside a stage. */
    bg:      '#0a0a0a',
    /** A panel lifted off the ground. */
    raised:  '#141414',
    /** A control or chip on top of a panel. */
    raised2: '#22251c',

    /** Primary text. */
    ink:     '#fafafa',
    /** Secondary text — labels, captions, annotations. */
    muted:   '#aeaea9',
    /** Tertiary — only for text that may be missed without loss. */
    dim:     '#939b96',

    /** Success, "it ran", live values. */
    green:   '#92bda5',
    /** Data, links, measured quantities. */
    cyan:    '#8fbbc5',
    /** Warnings, in-progress, warm emphasis. */
    orange:  '#e9956f',
    /** A second categorical accent. */
    pink:    '#d8a1b9',
    /** Secondary categorical accent. */
    violet:  '#b6a5d8',
    /** Errors and failures. */
    red:     '#ef9386',
    /** Highlights and callouts. */
    yellow:  '#dfc48a',

    rule:    'rgba(244,238,225,0.18)',
} as const;

/**
 * Keys stay typed; values widen to `string`.
 *
 * With a bare `as const` every entry is its own literal type, so a variable
 * initialised from `STAGE.muted` could never be reassigned to `STAGE.green` —
 * which is exactly what a lab does when it swaps a colour by state.
 */
export const STAGE: { [K in keyof typeof RAW]: string } = RAW;

export type StageColour = keyof typeof RAW;

/** Fade a stage colour. Works on the hex values above, unlike `color-mix` on a var. */
export function stageAlpha(colour: string, a: number): string {
    if (!colour.startsWith('#') || colour.length !== 7) return colour;
    const r = parseInt(colour.slice(1, 3), 16);
    const g = parseInt(colour.slice(3, 5), 16);
    const b = parseInt(colour.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
}
